<template>
  <header class="p-topbar">
    <div class="p-topbar__left">
      <button
        type="button"
        class="p-topbar__hamburger"
        :class="{ 'is-on': collapsed }"
        :title="collapsed ? '사이드바 펼치기' : '사이드바 접기'"
        @click="toggleSidebar"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      </button>

      <div class="p-welcome-pill">
        <span class="p-welcome-pill__wave">👋</span>
        <span>{{ greeting }},</span>
        <span class="p-welcome-pill__user">{{ userName }}</span>
      </div>
    </div>

    <div class="p-topbar__right">
      <span v-if="envBadge" class="p-topbar__env" :class="`p-topbar__env--${envKind}`">
        <span class="p-topbar__env-dot" />
        {{ envBadge }}
      </span>

      <!-- Language dropdown -->
      <div class="p-topbar__menu" :class="{ 'is-open': showLangMenu }">
        <button
          ref="langBtn"
          type="button"
          class="p-topbar__icon"
          :class="{ 'is-active': showLangMenu }"
          :title="`${$t ? ($t('label.language') || '언어') : '언어'} (${currentLangCode})`"
          aria-haspopup="menu"
          :aria-expanded="showLangMenu"
          @click="toggleLangMenu"
        >
          <i class="material-icons-round">language</i>
        </button>

        <div
          v-if="showLangMenu"
          ref="langMenu"
          class="p-topbar__dropdown"
          role="menu"
        >
          <div class="p-topbar__dropdown-head">언어 / Language</div>
          <button
            v-for="item in languages"
            :key="item.lan"
            type="button"
            role="menuitem"
            class="p-topbar__lang-item"
            :class="{ 'is-current': item.lan === currentLang }"
            @click="onLangSelect(item.lan)"
          >
            <span class="p-topbar__lang-flag">{{ item.flag }}</span>
            <span class="p-topbar__lang-text">
              <span class="p-topbar__lang-native">{{ item.native }}</span>
              <span class="p-topbar__lang-en">{{ item.en }}</span>
            </span>
            <i v-if="item.lan === currentLang" class="material-icons-round p-topbar__lang-check">check</i>
          </button>
        </div>
      </div>

      <button class="p-topbar__icon" type="button" :title="$t ? ($t('label.notifications') || '알림') : '알림'" @click="$emit('notifications-click')">
        <i class="material-icons-round">notifications</i>
      </button>
    </div>
  </header>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'AdminTopbar',
  emits: ['notifications-click'],
  data() {
    return {
      showLangMenu: false,
      // 옵션 A: 번역 커버리지 100% 보장. JP/ZH/ES는 추후 번역 작업 후 복귀 예정.
      languages: [
        { lan: 'korean',  native: '한국어',  en: 'Korean',  flag: '🇰🇷', code: 'KO' },
        { lan: 'english', native: 'English', en: 'English', flag: '🇺🇸', code: 'EN' },
      ],
    };
  },
  computed: {
    ...mapGetters('layout', ['isSidebarCollapsed']),
    collapsed() { return this.isSidebarCollapsed; },
    userName() {
      const u = this.$store.state.auth?.user;
      return u?.name || u?.username || u?.email?.split('@')[0] || 'Admin';
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 5) return '늦은 밤이에요';
      if (h < 12) return '좋은 아침이에요';
      if (h < 18) return '안녕하세요';
      return '좋은 저녁이에요';
    },
    envBadge() {
      const mode = this.$CONF?.NODE_ENV || process.env.VUE_APP_MODE || '';
      if (!mode || mode === 'production') return '';
      if (mode === 'development') return 'DEV';
      if (mode === 'staging') return 'STG';
      return mode.toUpperCase();
    },
    envKind() {
      const mode = this.$CONF?.NODE_ENV || process.env.VUE_APP_MODE || '';
      if (mode === 'development') return 'dev';
      if (mode === 'staging') return 'stg';
      return 'default';
    },
    currentLang() {
      return this.$store.state.language?.lang || 'english';
    },
    currentLangCode() {
      const found = this.languages.find(l => l.lan === this.currentLang);
      return found ? found.code : '??';
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocClick);
    document.addEventListener('keydown', this.onDocKey);
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onDocClick);
    document.removeEventListener('keydown', this.onDocKey);
  },
  methods: {
    ...mapActions('layout', ['toggleSidebar']),
    toggleLangMenu() {
      this.showLangMenu = !this.showLangMenu;
    },
    onLangSelect(lan) {
      this.$store.commit('language/changeLanguage', lan);
      this.showLangMenu = false;
    },
    onDocClick(e) {
      if (!this.showLangMenu) return;
      const btn = this.$refs.langBtn;
      const menu = this.$refs.langMenu;
      if (btn && btn.contains(e.target)) return;
      if (menu && menu.contains(e.target)) return;
      this.showLangMenu = false;
    },
    onDocKey(e) {
      if (e.key === 'Escape' && this.showLangMenu) {
        this.showLangMenu = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/pineapple';

.p-topbar {
  position: sticky;
  top: 0;
  z-index: 900;
  height: 56px;
  background: $p-panel;
  border-bottom: 1px solid $p-line;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  gap: 16px;
}

.p-topbar__left {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.p-topbar__right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.p-topbar__hamburger {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border: 1px solid $p-line;
  background: $p-panel;
  color: $p-ink;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

  &:hover { background: $p-panel-alt; border-color: $p-line-hard; }

  &.is-on {
    background: $p-point-soft;
    border-color: $p-point-deep;
    color: $p-point-deep;
  }
}

.p-topbar__env {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 10px 0 9px;
  border-radius: 999px;
  font-family: $p-font-mono;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  border: 1px solid;

  .p-topbar__env-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  &--dev    { background: $p-info-soft; color: $p-info;  border-color: rgba(59, 123, 196, 0.35); }
  &--stg    { background: $p-warn-soft; color: $p-warn;  border-color: rgba(201, 138, 10, 0.35); }
  &--default{ background: $p-panel-alt; color: $p-ink-mid; border-color: $p-line; }
}

.p-topbar__icon {
  position: relative;
  display: inline-grid;
  place-items: center;
  width: 36px;
  height: 36px;
  background: $p-panel;
  border: 1px solid $p-line;
  color: $p-ink;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;

  &:hover { background: $p-panel-alt; border-color: $p-line-hard; }

  &.is-active {
    background: $p-point-soft;
    border-color: $p-point-deep;
    color: $p-point-deep;
  }

  i { font-size: 18px; }
}

// Dropdown
.p-topbar__menu {
  position: relative;
}

.p-topbar__dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 220px;
  background: $p-panel;
  border: 1px solid $p-line;
  border-radius: $p-radius;
  box-shadow: 0 12px 32px rgba(31, 27, 20, 0.16);
  padding: 6px;
  z-index: 1000;
}

.p-topbar__dropdown-head {
  padding: 6px 10px 8px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $p-ink-soft;
  border-bottom: 1px solid $p-line-soft;
  margin-bottom: 4px;
}

.p-topbar__lang-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-radius: $p-radius-sm;
  cursor: pointer;
  text-align: left;
  font-family: $p-font-body;
  color: $p-ink;
  transition: background 0.1s ease;

  &:hover { background: $p-panel-alt; }

  &.is-current {
    background: $p-point-soft;
    .p-topbar__lang-native { color: $p-point-deep; font-weight: 800; }
  }
}

.p-topbar__lang-flag {
  font-size: 18px;
  line-height: 1;
}

.p-topbar__lang-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.p-topbar__lang-native {
  font-size: 13px;
  font-weight: 700;
  color: $p-ink;
}

.p-topbar__lang-en {
  font-size: 11px;
  color: $p-ink-soft;
}

.p-topbar__lang-check {
  font-size: 16px !important;
  color: $p-point-deep;
}
</style>
